import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { normalizarTexto } from '../../../../core/utils/texto.util';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { ResgateDocumentoOpcao } from '../../../../core/models/parametros/resgate-configuracao.model';

/**
 * Modal de "Adicionar documento" sobre o `vivest-modal` do design system: lista os documentos
 * disponíveis, com busca e seleção múltipla. O Esc é tratado pelo próprio modal (`dismissible`).
 * Documentos que já estão na configuração aparecem desabilitados.
 */
@Component({
  selector: 'app-resgate-documentos-modal',
  standalone: true,
  imports: [VivestModalAreaVisivelDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './resgate-documentos-modal.component.html',
  styleUrl: './resgate-documentos-modal.component.scss',
})
export class ResgateDocumentosModalComponent {
  private readonly campoBusca = viewChild<ElementRef<HTMLInputElement>>('campoBusca');

  readonly opcoes = input.required<readonly ResgateDocumentoOpcao[]>();
  /** Ids dos documentos que já estão na configuração. */
  readonly jaIncluidos = input<readonly number[]>([]);
  readonly carregando = input(false);

  readonly adicionar = output<ResgateDocumentoOpcao[]>();
  readonly fechar = output<void>();

  readonly busca = signal('');
  readonly selecionados = signal<ReadonlySet<number>>(new Set());

  private readonly incluidos = computed(() => new Set(this.jaIncluidos()));

  readonly opcoesFiltradas = computed(() => {
    const termo = normalizarTexto(this.busca());
    return this.opcoes().filter((opcao) => !termo || normalizarTexto(opcao.nome).includes(termo));
  });

  readonly totalSelecionado = computed(() => this.selecionados().size);

  constructor() {
    // Leva o foco para a busca ao abrir, para o modal já poder ser usado pelo teclado.
    afterNextRender(() => this.campoBusca()?.nativeElement.focus());
  }

  jaIncluido(opcao: ResgateDocumentoOpcao): boolean {
    return this.incluidos().has(opcao.id);
  }

  estaSelecionado(opcao: ResgateDocumentoOpcao): boolean {
    return this.selecionados().has(opcao.id);
  }

  alternar(opcao: ResgateDocumentoOpcao): void {
    if (this.jaIncluido(opcao)) {
      return;
    }
    const proximos = new Set(this.selecionados());
    if (proximos.has(opcao.id)) {
      proximos.delete(opcao.id);
    } else {
      proximos.add(opcao.id);
    }
    this.selecionados.set(proximos);
  }

  aoBuscar(valor: string): void {
    this.busca.set(valor);
  }

  confirmar(): void {
    const escolhidos = this.opcoes().filter((opcao) => this.selecionados().has(opcao.id));
    if (escolhidos.length) {
      this.adicionar.emit(escolhidos);
    }
  }

  onFechar(): void {
    this.fechar.emit();
  }
}
